import { Plus, Trash2 } from 'lucide-react';
import type { JSX } from 'react';
import type { FieldErrors, UseFieldArrayReturn, UseFormRegister } from 'react-hook-form';
import { PageSection } from '@/components/layout/page.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';
import type { OperationFormValues } from './operation-form.js';

/**
 * Produits ajoutés par une opération (`02` §10) : SKU, quantité, prix — une
 * ligne CSV chacun. Même gabarit que les composants d'un bundle ; une dernière
 * ligne ne peut pas être retirée, une opération sans produit n'a pas d'effet.
 */
export interface OperationProductsTableProps {
  register: UseFormRegister<OperationFormValues>;
  fieldArray: UseFieldArrayReturn<OperationFormValues, 'products'>;
  errors: FieldErrors<OperationFormValues>;
}

function CellError({ message }: { message: string | undefined }): JSX.Element | null {
  return message ? <p className="mt-1.5 text-xs text-status-red-fg">{message}</p> : null;
}

export function OperationProductsTable({
  register,
  fieldArray,
  errors,
}: OperationProductsTableProps): JSX.Element {
  const { fields, append, remove } = fieldArray;

  return (
    <PageSection
      title="Produits ajoutés"
      flush
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            append({ sku: '', qty: 1, unitPrice: '0' });
          }}
        >
          <Plus aria-hidden="true" />
          Ajouter un produit
        </Button>
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>SKU</TableHead>
            <TableHead className="w-28">Quantité</TableHead>
            <TableHead className="w-40">Prix</TableHead>
            <TableHead className="w-12">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.map((field, index) => {
            const rowErrors = errors.products?.[index];
            const position = String(index + 1);
            return (
              <TableRow key={field.id} className="hover:bg-transparent">
                <TableCell className="align-top">
                  <Input
                    placeholder="SKU"
                    className="min-w-40"
                    aria-label={`SKU du produit ${position}`}
                    aria-invalid={rowErrors?.sku ? true : undefined}
                    {...register(`products.${index}.sku` as const)}
                  />
                  <CellError message={rowErrors?.sku?.message} />
                </TableCell>
                <TableCell className="align-top">
                  <Input
                    type="number"
                    min={1}
                    className="w-24"
                    aria-label={`Quantité du produit ${position}`}
                    aria-invalid={rowErrors?.qty ? true : undefined}
                    {...register(`products.${index}.qty` as const, { valueAsNumber: true })}
                  />
                  <CellError message={rowErrors?.qty?.message} />
                </TableCell>
                <TableCell className="align-top">
                  <Input
                    className="w-36"
                    aria-label={`Prix du produit ${position}`}
                    aria-invalid={rowErrors?.unitPrice ? true : undefined}
                    {...register(`products.${index}.unitPrice` as const)}
                  />
                  <CellError message={rowErrors?.unitPrice?.message} />
                </TableCell>
                <TableCell className="align-top text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Supprimer le produit ${position}`}
                    disabled={fields.length <= 1}
                    onClick={() => {
                      remove(index);
                    }}
                  >
                    <Trash2 aria-hidden="true" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </PageSection>
  );
}
